import { motion } from "framer-motion";
import { MapPin, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const showrooms = [
  {
    name: "South Charlotte Showroom",
    address: "9716-C Rea Road",
    city: "Charlotte, NC 28277",
    phone: "(704) 419-8897",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&auto=format&fit=crop",
  },
  {
    name: "Lake Norman Showroom",
    address: "9606-A Bailey Rd",
    city: "Cornelius, NC 28031",
    phone: "(803) 590-8182",
    image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=600&auto=format&fit=crop",
  },
  {
    name: "Charlotte Warehouse",
    address: "801-C Atando Ave",
    city: "Charlotte, NC 28206",
    phone: "(704) 480-3855",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&auto=format&fit=crop",
  },
];

const ShowroomsSection = () => {
  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-primary font-semibold text-sm uppercase tracking-wider">
            Visit Us
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
            Our Locations
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Experience our flooring collections in person at one of our three convenient locations 
            in the Charlotte metro area.
          </p>
        </motion.div>

        {/* Hours Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-muted rounded-xl p-6 mb-12 flex flex-wrap items-center justify-center gap-6 text-center"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            <span className="font-semibold text-foreground">Showroom Hours:</span>
          </div>
          <div className="flex flex-wrap gap-4 text-muted-foreground">
            <span>Mon-Fri: 10AM-6PM</span>
            <span className="hidden sm:inline">•</span>
            <span>Sat: 10AM-4PM</span>
            <span className="hidden sm:inline">•</span>
            <span>Sun: Closed</span>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {showrooms.map((location, index) => (
            <motion.div
              key={location.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-muted rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={location.image}
                  alt={location.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {location.name}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="text-muted-foreground text-sm">
                      <p>{location.address}</p>
                      <p>{location.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                    <a
                      href={`tel:${location.phone.replace(/[^0-9]/g, "")}`}
                      className="text-foreground hover:text-primary transition-colors font-medium"
                    >
                      {location.phone}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button asChild size="lg">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ShowroomsSection;
